import type { GenerationConfig } from '../types';

/**
 * The configuration a card-set submission sends, and the one later sends reuse.
 *
 * The request that submits a card set names the turn that asked
 * (`answeredRequestId`), because the server records the answers as a claim on
 * that card set and turns away a second confirmation of it — two tabs that both
 * watched the card set arrive used to start two generations, and a confirmation
 * after 停止 → 新建对话 landed in the new conversation. See clarifyClaim.ts in
 * the gateway.
 *
 * The name is the asking turn's **requestId**, not the browser's own message
 * id: the browser's id is `(Date.now() + 1).toString()` and the server's row is
 * `${requestId}:assistant`, so a claim keyed on the browser's id missed every
 * live confirmation (2026-09-16). The requestId is the one identifier both
 * sides hold.
 *
 * The name belongs to that one request. The configuration ChatArea keeps for
 * retry, 继续 and plan approval must not carry it: those requests are not
 * answers, and a name riding along would make the server read a retry after a
 * quota rejection as a duplicate of the very confirmation it is retrying.
 */
export function splitClarifySubmitConfig(
  followUpConfig: GenerationConfig,
  answeredRequestId: string,
): { reusable: GenerationConfig; request: GenerationConfig } {
  const state = followUpConfig.clarifyState;
  if (!state) return { reusable: followUpConfig, request: followUpConfig };

  const { answeredRequestId: _stale, ...unnamed } = state;
  const reusable: GenerationConfig = { ...followUpConfig, clarifyState: unnamed };
  const name = answeredRequestId.trim();
  const request: GenerationConfig = {
    ...followUpConfig,
    clarifyState: name ? { ...unnamed, answeredRequestId: name } : { ...unnamed },
  };
  return { reusable, request };
}
